// FIN-05 cash custody (`cash-custody.v1`) — wire types and exact minor-unit helpers for the Cash page.
//
// Amounts arrive as decimal-digit strings in integer minor units with the pinned ISO exponent. They are
// formatted and parsed by string manipulation only: no Number/parseFloat, no FX, no rounding, no
// fallback currency. A value that does not fit the contract renders as "unknown", never as 0.

export type CashMode = "enabled" | "draining" | "off";

export interface CashCustodyStatus {
  contract_version: "cash-custody.v1";
  mode: CashMode;
  enabled: boolean;
  backlog: boolean;
  allowed_actions: string[];
  currency_table_version: string;
}

export interface CashParty {
  kind: string;
  id: string;
  name?: string | null;
}

export interface CashPosition {
  holder: CashParty;
  beneficiary: CashParty;
  currency: string;
  currency_exponent: number;
  outstanding_minor: string;
  reserved_minor: string;
  available_minor: string;
}

export interface CashCurrencyTotal {
  currency: string;
  currency_exponent: number;
  outstanding_minor: string;
  reserved_minor: string;
  available_minor: string;
}

export interface CashHandoverReceipt {
  kind: "cash_handover";
  id: string;
  state: "pending_acknowledgement" | "acknowledged" | "disputed" | "declined" | "cancelled";
  currency: string;
  currency_exponent: number;
  amount_minor: string;
  received_minor: string | null;
  custody_moved: boolean;
  cash_with: "giver" | "receiver" | "split";
  from: CashParty;
  to: CashParty;
  beneficiary: CashParty;
  note: string | null;
  requested_at: string | null;
  responded_at: string | null;
  response_note: string | null;
  reassigned_by?: string | null;
  to_me?: boolean;
  from_me?: boolean;
}

export interface CashCollectionReceipt {
  kind: "cash_collection";
  id: string;
  delivery_id: string;
  state: "collected" | "partial" | "disputed";
  currency: string;
  currency_exponent: number;
  due_minor: string;
  amount_minor: string;
  receivable_minor: string;
  holder: CashParty;
  holder_name?: string | null;
  collected_at: string | null;
  dispute: { reason: string | null; resolved_at: string | null; resolution_note: string | null } | null;
  shortfall_reason?: string | null;
  receivable?: { state: "open" | "recovered" | "written_off"; settled_at: string | null; settlement_note: string | null } | null;
}

export interface CashDayRow {
  holder: CashParty;
  currency: string;
  currency_exponent: number;
  day: string;
  outstanding_minor: string;
}

export interface CashInvariantBreak {
  holder: CashParty;
  currency: string;
  beneficiary: CashParty;
  stored_minor: string | null;
  derived_minor: string;
  reason: string;
}

export interface CashInvariant {
  ok: boolean;
  checked_positions: number;
  breaks: CashInvariantBreak[];
}

export interface CashWorkspace {
  cash_custody: CashCustodyStatus;
  as_of: string;
  me: { id: string; role: string; can_receive: boolean; can_move_money: boolean };
  by_currency: CashCurrencyTotal[];
  holders: CashPosition[];
  by_day: CashDayRow[];
  /** IANA zone the day buckets use (the shop's timezone). */
  day_basis?: string;
  aging_complete: boolean;
  pending_handovers: CashHandoverReceipt[];
  disputes: { collections: CashCollectionReceipt[]; handovers: CashHandoverReceipt[] };
  receivables: {
    by_currency: { currency: string; currency_exponent: number; stops: number; receivable_minor: string }[];
    stops: CashCollectionReceipt[];
  };
  my_holdings: CashPosition[];
  /** Holders who left / were demoted / deactivated and still hold unreserved cash (owner/admin reassign). */
  departed_holdings?: CashPosition[];
  /** Console users this operator can hand cash to. */
  receivers?: { id: string; name: string; role: string }[];
  coverage: { positions_limit: number; positions_truncated?: boolean; list_limit: number };
  return_cash_policy: "no_cash_refund";
}

const MINOR = /^(0|[1-9][0-9]{0,17})$/;
const CURRENCY = /^[A-Z]{3}$/;

/** `12345` minor with exponent 2 → `123.45`. Exact; unknown input → null. */
export function minorToDecimal(minor: unknown, exponent: unknown): string | null {
  if (typeof minor !== "string" || !MINOR.test(minor)) return null;
  if (typeof exponent !== "number" || !Number.isInteger(exponent) || exponent < 0 || exponent > 4) return null;
  if (exponent === 0) return minor;
  const padded = minor.padStart(exponent + 1, "0");
  return `${padded.slice(0, padded.length - exponent)}.${padded.slice(padded.length - exponent)}`;
}

export function formatCash(minor: unknown, exponent: unknown, currency: unknown): string {
  const amount = minorToDecimal(minor, exponent);
  const cur = typeof currency === "string" && CURRENCY.test(currency) ? currency : null;
  if (amount === null) return cur ? `Amount unknown (${cur})` : "Amount and currency unknown";
  return cur ? `${cur} ${amount}` : `${amount} (currency unknown)`;
}

/** Operator-typed decimal text → minor-unit string; excess precision or junk → null (never rounded). */
export function decimalToMinor(text: string, exponent: number): string | null {
  const t = text.trim();
  const m = /^([0-9]{1,15})(?:\.([0-9]+))?$/.exec(t);
  if (!m || !Number.isInteger(exponent) || exponent < 0 || exponent > 4) return null;
  const whole = m[1];
  const frac = m[2] ?? "";
  if (frac.length > exponent && /[1-9]/.test(frac.slice(exponent))) return null;
  const digits = (whole + frac.slice(0, exponent).padEnd(exponent, "0")).replace(/^0+(?=[0-9])/, "");
  return MINOR.test(digits) ? digits : null;
}

/** Compare two canonical minor-unit strings without Number conversion. */
export function compareMinor(a: string, b: string): number {
  if (a.length !== b.length) return a.length < b.length ? -1 : 1;
  return a < b ? -1 : a > b ? 1 : 0;
}

export function partyLabel(party: CashParty | undefined | null): string {
  if (!party) return "Unknown holder";
  const role = party.kind === "rider" ? "rider" : party.kind === "operator" ? "console" : party.kind;
  return `${party.name || party.id.slice(0, 8)} (${role})`;
}

export function newIdempotencyKey(prefix: string): string {
  const random = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}-${random}`;
}

/**
 * Whether a failed money write may be retried with a NEW idempotency key. Only a definitive refusal (4xx: the
 * server rejected the request without recording it) rotates the key. A 5xx / gateway timeout / network failure is
 * an UNKNOWN outcome — the server may already have committed — so the retry must reuse the same key (MC-28).
 */
export function mayRotateIdempotencyKey(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" && status >= 400 && status < 500;
}
